// Libraries
import { useTranslation } from "react-i18next";
import { BlobServiceClient } from "@azure/storage-blob";
import { useCallback, useMemo, useState } from "react";

// Hooks
import { useProgressionAccessInfo } from "src/services/exams/hooks/progression-reports/useProgressionAccessInfo";

// Contexts
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Constants
import { DEFAULT_REPORT_TYPE, ReportFileType } from "src/constants/enums";

// Types
import { FileInfoType } from "src/types/sharedTypes";

type useOnDemandProgressionReportGenerationType = {
  patientId: string;
  analysisId: string;
};

const initialFileInfo: FileInfoType = {
  hasBlobStorageError: false,
  isDownloading: false,
  isPrinting: false,
  accessInfo: null,
  reportInfoData: null,
};

// Mirrors useOnDemandReportGeneration -- same "the file already exists in
// blob storage, just fetch and download it" flow, swapped to the
// progression-analysis access-info endpoint (analysisId instead of examId).
const useOnDemandProgressionReportGeneration = ({
  patientId,
  analysisId,
}: useOnDemandProgressionReportGenerationType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();
  const { setSnackbarInfo } = useSnackbarContext();

  const [fileInfo, setFileInfo] = useState<FileInfoType>(initialFileInfo);

  const fileType = useMemo(
    () => clinicInfo?.settings?.defaultReportType ?? DEFAULT_REPORT_TYPE,
    [clinicInfo?.settings?.defaultReportType]
  );

  const { mutateAsync: getAccessInfoAPI } = useProgressionAccessInfo(
    clinicInfo?.id
  );

  const getAccessInfo = useCallback(
    async (fileType: ReportFileType) =>
      await getAccessInfoAPI({ patientId, analysisId, fileType }),
    [patientId, analysisId, getAccessInfoAPI]
  );

  // Retrieve access information for blob storage
  const getBlobAccessInfo = useCallback(async () => {
    try {
      const accessInfo = await getAccessInfo(fileType);

      setFileInfo((prev) => {
        const newFileInfo = { ...prev };
        if (newFileInfo?.hasBlobStorageError !== undefined)
          newFileInfo.hasBlobStorageError = false;
        if (newFileInfo?.accessInfo !== undefined)
          newFileInfo.accessInfo = accessInfo;
        return newFileInfo;
      });

      return accessInfo;
    } catch (e) {
      if (fileInfo?.hasBlobStorageError) {
        setFileInfo((prev) => {
          const newFileInfo = { ...prev };
          if (newFileInfo?.hasBlobStorageError)
            newFileInfo.hasBlobStorageError = true;
          return newFileInfo;
        });
      }
      throw e;
    }
  }, [fileType, fileInfo, getAccessInfo]);

  const downloadPdfBlob = async () => {
    setFileInfo((prev) => {
      const newFileInfo = { ...prev };
      if (newFileInfo?.isDownloading !== undefined)
        newFileInfo.isDownloading = true;
      return newFileInfo;
    });

    try {
      const accessInfo = await getBlobAccessInfo();
      const access = accessInfo?.access;

      if (!accessInfo || !access) {
        setFileInfo((prev) => {
          const newFileInfo = { ...prev };
          if (newFileInfo?.isDownloading !== undefined)
            newFileInfo.isDownloading = false;
          return newFileInfo;
        });
        throw new Error("Invalid access information");
      }

      const blobServerClient = new BlobServiceClient(
        `${access?.url}${access.sasKey}`
      );
      const containerClient = blobServerClient.getContainerClient(
        access.containerName
      );
      const blobClient = containerClient
        .getBlobClient(accessInfo.fileName)
        .getBlockBlobClient();

      const downloadedRes = await blobClient.download();
      const blob = await downloadedRes.blobBody;

      if (blob) {
        setFileInfo((prev) => {
          const newFileInfo = { ...prev };
          if (newFileInfo?.isDownloading !== undefined)
            newFileInfo.isDownloading = false;
          return newFileInfo;
        });
        return {
          blob: blob,
          fileName: accessInfo.recommendedFileName,
        };
      }
      return null;
    } catch (e) {
      // display error toast if PDF download fails
      setSnackbarInfo({
        variant: "error",
        message: t("Failed to download PDF!"),
      });
      console.error(e);
      return null;
    } finally {
      setFileInfo((prev) => {
        const newFileInfo = { ...prev };
        if (newFileInfo?.isDownloading !== undefined)
          newFileInfo.isDownloading = false;
        return newFileInfo;
      });
    }
  };

  const downloadFile = async () => {
    const blobData = await downloadPdfBlob();

    if (!blobData) {
      // warning modal
      return;
    }

    const blob = blobData.blob;

    if (blob && blob.size > 0) {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);

      link.download = blobData.fileName;
      link.click();
    } else {
      // warning modal
    }
  };

  return {
    downloadFile,
  };
};

export default useOnDemandProgressionReportGeneration;
